// import { EMAIL_VALIDATE_REGEX } from '@/configs/constants';
import CloudUpload from '@/assets/icons/CloudUpload';
import FileIcon from '@/assets/icons/FileIcon';
import TrashIcon from '@/assets/icons/TrashIcon';
import { Button } from '@/components/common/Button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { toastify } from '@/utils/toastify';
import { cn } from '@/utils/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { FileDown } from 'lucide-react';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ShowResultAfterUploadFile from './ShowResultAfterUploadFile';
// import { useNavigate } from 'react-router-dom';

export default observer(function UploadFileForm() {
    // hooks
    const { t } = useTranslation();

    // const navigate = useNavigate();

    // store
    const {
        modalStore: { showAlertModal, hideModal },
        itemStore: { uploadFileExcel, getAllItem, paging }
    } = useStore();

    // CSV (推奨) + XLSX (旧テンプレ互換) を受け付ける。
    // MIME type はブラウザ/OS で揺れる (CSV が text/csv や application/vnd.ms-excel になる等)
    // ため拡張子で判定する。サーバ側も拡張子で CSV/XLSX を切り替える。
    const isAllowedFile = (file?: File | null) =>
        !!file && /\.(csv|xlsx)$/i.test(file.name);

    const FILE_TYPE_ERROR = 'CSV または XLSX ファイルのみが許可されます';

    // validate schema
    const validateSchema = yup.object().shape({
        file: yup
            .mixed()
            .required('form_error_validate.required')
            .test(
                'fileType',
                FILE_TYPE_ERROR,
                (value: any) => isAllowedFile(value as File)
            )
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema)
    });

    // state
    const [file, setFile] = useState<File | null>(null);
    const [isDragging, setIsDragging] = useState(false);

    // functions
    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const files = e.dataTransfer.files;
        if (!files || files.length === 0) return;

        const file = files[0];

        if (!isAllowedFile(file)) {
            form.setError('file', { message: FILE_TYPE_ERROR });
            setFile(null);
            return;
        }

        setFile(file);
        form.setValue('file', file);
        form.clearErrors('file');
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const file = files[0];

        if (!isAllowedFile(file)) {
            form.setError('file', { message: FILE_TYPE_ERROR });
            return;
        }

        setFile(file);
        form.setValue('file', file);
        form.clearErrors('file');
    };

    const removeFile = () => {
        setFile(null);
        form?.resetField('file');
        form.setError('file', { message: t('form_error_validate.required') });
    };

    const onSubmit = async (data?: any) => {
        const res = await flowResult(uploadFileExcel(data));
        if (res?.ok) {
            setFile(null);
            getAllItem(undefined, paging);
            hideModal();
            showAlertModal({
                footerClassName: 'hidden',
                title: t('words_title.show_result_after_upload_data'),
                size: '3xl',
                contentClassName: 'pb-0 !overflow-scroll',
                headerClassName: 'border-b border-b-border-7',
                content: <ShowResultAfterUploadFile />
            });
        } else {
            toastify('error', res?.message);
        }
    };

    const downloadFile = (fileUrl: string, filename: string) => {
        const link = document.createElement('a');
        // public/ 配下は Cache-Control ヘッダが無く heuristic キャッシュされるため、
        // サンプル差し替え後もブラウザが旧版を配ってしまう。取得ごとに一意の query を
        // 付けて常に最新を落とす (2026-07-23: 旧サンプルで検証が空回りした実害あり)。
        link.href = `${fileUrl}?t=${Date.now()}`;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <>
            {
                !file && (
                    <div className='mb-4'>
                        <p className='text-black-2'>{t('field.download_file')}</p>
                        <div className='flex items-center space-x-2 mt-2'>
                            <Button onClick={() => downloadFile('/SampleData.csv', 'SampleData.csv')} size='xl' variant='active' className='h-[34px] w-[190px] flex items-center space-x-1 !rounded-[8px]'>
                                <FileDown color='#FFFFFF' size={20} />
                                <p className='text-[15px]'>{t('button.download_file_csv')}</p>
                            </Button>
                            <Button onClick={() => downloadFile('/SampleData.xlsx', 'SampleData.xlsx')} size='xl' variant='outline' className='h-[34px] w-[190px] flex items-center space-x-1 !rounded-[8px]'>
                                <FileDown size={20} />
                                <p className='text-[15px]'>{t('button.download_file_xlsx')}</p>
                            </Button>
                        </div>
                        <p className='text-[13px] text-text-gray-1 mt-2 whitespace-pre-line'>{t('sentences.upload_file_note')}</p>
                    </div>
                )
            }
            <div>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                        <div className='h-full'>
                            <FormField
                                control={form.control}
                                name='file'
                                render={({ field }) => (
                                    <div className='h-full flex flex-col'>
                                        <Label className='mb-2'>
                                            {t('field.upload_file')}
                                        </Label>
                                        <FormItem className='w-full flex-1'>
                                            <FormControl>
                                                <div
                                                    className={`h-full w-full border-[1.5px] flex flex-col items-center justify-center relative ${form?.formState?.errors.file ?
                                                        'border-[#ef4444] rounded-lg py-8' :
                                                        !file ?
                                                            'border-input bg-background-input rounded-lg py-8' :
                                                            'border-none py-0'}`}
                                                    onDragOver={handleDragOver}
                                                    onDrop={(e) => {
                                                        handleDrop(e);
                                                        field.onChange(e.dataTransfer.files[0]);
                                                    }}
                                                    onDragLeave={handleDragLeave}
                                                >
                                                    {file ?
                                                        (
                                                            <div className='flex items-center h-[61px] justify-between w-full p-4 border border-input rounded-lg bg-background-input'>
                                                                <div className='flex items-center space-x-2'>
                                                                    <div className='flex items-center justify-center'>
                                                                        <FileIcon />
                                                                    </div>

                                                                    <div>
                                                                        <p className='text-sm font-medium text-active'>
                                                                            {file?.name}
                                                                        </p>
                                                                    </div>
                                                                </div>

                                                                {/* Nút xoá */}
                                                                <Button
                                                                    onClick={removeFile}
                                                                    size='icon'
                                                                    variant='ghost'
                                                                    className='rounded-full bg-background-inactive'
                                                                >
                                                                    <TrashIcon className='size-[20px]' color='#FE1212' />
                                                                </Button>
                                                            </div>

                                                        ) :
                                                        (
                                                            <label htmlFor='file-input' className='h-full w-full rounded-lg flex flex-col justify-center items-center bg-background-input'>
                                                                <Input
                                                                    type='file'
                                                                    id='file-input'
                                                                    className='hidden h-full w-full'
                                                                    accept='.csv,.xlsx'
                                                                    onChange={(e) => {
                                                                        handleFileChange(e);
                                                                        field.onChange(e.target.files?.[0]);
                                                                    }}
                                                                />
                                                                <div
                                                                    className='cursor-pointer flex flex-col items-center space-x-2 text-gray-600 text-sm'
                                                                >
                                                                    <CloudUpload />
                                                                </div>
                                                                <div className='text-[15px] text-center text-text-gray-1 whitespace-pre-line'>
                                                                    <p className='mt-[10px]'>
                                                                        {t('sentences.upload_file')}
                                                                    </p>
                                                                </div>
                                                            </label>
                                                        )}
                                                </div>
                                            </FormControl>
                                        </FormItem>
                                        <FormMessage className='mt-1' />
                                    </div>
                                )}
                            />
                        </div>
                        <div className='mt-6 flex justify-center items-center'>
                            <Button
                                type='submit'
                                variant='active'
                                className={cn('w-[166.5px] text-[15px] font-semibold h-12 bg-active hover:opacity-80 hover:text-white transition-opacity duration-300 !rounded-[10px]')}
                            >
                                {t('button.upload_file')}
                            </Button>
                        </div>
                    </form>
                </Form>
            </div>
        </>
    );
});
